import { useState, useCallback, useRef, useEffect } from 'react';
import { apiGet } from './apiClient';
import { indexerTradeToMarketTrade } from './sidebarTradeTypes';
import type { IndexerTradeItem } from './sidebarTradeTypes';
import type { MarketTrade } from './useMarketTrades';

interface TraderTradesCacheResult {
    tradesByAccount: Map<string, MarketTrade[]>;
    loadingAccounts: Set<string>;
    loadTrader: (account: string) => void;
    clearCache: () => void;
}

interface TradesResponse {
    event_id: string;
    market_ids: string[];
    trades: IndexerTradeItem[];
    trade_count: number;
    has_more: boolean;
}

export function useTraderTradesCache(
    conditionId: string | null,
    eventId: string | null,
    fromDate?: string | null,
    toDate?: string | null
): TraderTradesCacheResult {
    const [tradesByAccount, setTradesByAccount] = useState<
        Map<string, MarketTrade[]>
    >(new Map());
    const [loadingAccounts, setLoadingAccounts] = useState<Set<string>>(
        new Set()
    );
    const inflight = useRef(new Set<string>());
    // One controller per in-flight account fetch. `loadTrader` can be called for
    // several distinct traders in close succession (e.g. loading all URL-provided
    // traders, or toggling several highlights), so a single shared controller would
    // abort trader A's still-valid request when trader B's load starts. Abort scope
    // is per-account instead, only torn down on clearCache or unmount.
    const abortControllers = useRef(new Map<string, AbortController>());

    const loadTrader = useCallback(
        (account: string) => {
            if (!conditionId || !eventId) return;
            if (tradesByAccount.has(account)) return;
            if (inflight.current.has(account)) return;

            inflight.current.add(account);
            setLoadingAccounts(prev => new Set([...prev, account]));

            const controller = new AbortController();
            abortControllers.current.set(account, controller);

            const addr = account.replace(/^polymarket:/, '');
            const params = new URLSearchParams({
                markets: conditionId,
                accounts: addr,
                limit: '5000',
            });
            if (fromDate) params.set('from_ts', fromDate);
            if (toDate) params.set('to_ts', toDate);

            apiGet<TradesResponse>(
                `/api/trades/event/${encodeURIComponent(eventId)}?${params}`,
                controller.signal
            )
                .then(res => {
                    if (controller.signal.aborted) return;
                    const trades = res.trades.map(indexerTradeToMarketTrade);
                    setTradesByAccount(prev => {
                        const next = new Map(prev);
                        next.set(account, trades);
                        return next;
                    });
                })
                .catch(err => {
                    if (err?.name === 'AbortError' || controller.signal.aborted)
                        return;
                    console.error(`Failed to load trades for ${account}:`, err);
                })
                .finally(() => {
                    if (abortControllers.current.get(account) === controller) {
                        abortControllers.current.delete(account);
                        inflight.current.delete(account);
                    }
                    setLoadingAccounts(prev => {
                        const next = new Set(prev);
                        next.delete(account);
                        return next;
                    });
                });
        },
        [conditionId, eventId, fromDate, toDate, tradesByAccount]
    );

    const clearCache = useCallback(() => {
        for (const controller of abortControllers.current.values()) {
            controller.abort();
        }
        abortControllers.current.clear();
        setTradesByAccount(new Map());
        setLoadingAccounts(new Set());
        inflight.current.clear();
    }, []);

    // Abort any in-flight fetches on unmount.
    useEffect(() => {
        const controllers = abortControllers.current;
        return () => {
            for (const controller of controllers.values()) {
                controller.abort();
            }
        };
    }, []);

    return { tradesByAccount, loadingAccounts, loadTrader, clearCache };
}
